import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    useGetRuleSetVersionQuery,
    useSaveRuleSetDraftMutation,
    useConfirmRuleSetMutation,
    useGetFileVersionsQuery,
    useToggleSourceFieldMutation,
    useUpdateProcessingIssueMutation,
} from '../../../redux/api/compassPortal';
import { errMsg, downloadFile } from './bits';

// The MS one-stop source view: click a source anywhere and see everything —
// what landed (file, origin, rows, versions), what's required, the RULES
// (view + edit), and any open issues — without babysitting CS for answers.
// Editing rules NEVER mutates the live version: a save creates the next DRAFT
// version and only the confirm gate (recorded name) makes it run.
export default function SourceDetail({ projectId, clientName, source: s, onClose }) {
    const token = useSelector((state) => state.oauth.auth0Token);
    const [editText, setEditText] = useState(null); // null = not editing
    const [msg, setMsg] = useState(null);
    const [savedDraft, setSavedDraft] = useState(null); // {version} after a save
    const [saveDraft, { isLoading: saving }] = useSaveRuleSetDraftMutation();
    const [confirmRuleSet, { isLoading: confirming }] = useConfirmRuleSetMutation();
    const [toggleField, { isLoading: toggling }] = useToggleSourceFieldMutation();
    const [updateIssue, { isLoading: issueBusy }] = useUpdateProcessingIssueMutation();
    const [blockText, setBlockText] = useState('');

    const ruleId = s?.rule_set_id;
    const { data: envelope, isFetching: rulesLoading, refetch: refetchRules } = useGetRuleSetVersionQuery(
        { clientName, ruleId, version: 'latest' }, { skip: !ruleId });
    const { data: fileVersions } = useGetFileVersionsQuery(
        { projectId, sourceId: s?.source_id }, { skip: !s?.source_id });

    useEffect(() => { setEditText(null); setMsg(null); setSavedDraft(null); }, [s?.source_id]);

    if (!s) return null;
    const recipe = envelope?.rule_set;
    const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

    const startEdit = () => { setMsg(null); setEditText(JSON.stringify(recipe, null, 2)); };
    const saveEdit = async () => {
        setMsg(null);
        let parsed;
        try { parsed = JSON.parse(editText); }
        catch (e) { setMsg(`⚠ Not valid JSON: ${e.message}`); return; }
        try {
            const r = await saveDraft({ clientName, ruleSet: parsed }).unwrap();
            setSavedDraft({ version: r.version, ruleId: r.rule_id });
            setEditText(null);
            setMsg(`Draft v${r.version} saved — confirm below to make it the live version.`);
            refetchRules();
        } catch (e) { setMsg(`⚠ ${errMsg(e)}`); }
    };
    const confirmLatest = async () => {
        setMsg(null);
        const version = savedDraft?.version ?? envelope?.version;
        const rid = savedDraft?.ruleId ?? ruleId;
        try {
            await confirmRuleSet({ clientName, ruleId: rid, version }).unwrap();
            setSavedDraft(null);
            setMsg(`v${version} confirmed — it's now the live rule set for this source.`);
            refetchRules();
        } catch (e) { setMsg(`⚠ ${errMsg(e)}`); }
    };

    return (
        <>
            <div className="cp-drawer-backdrop" onClick={onClose} />
            <aside className="cp-drawer" role="dialog" aria-label={`${s.name} details`}>
                <h3 style={{ marginTop: 0 }}>{s.name}</h3>
                <p className="cp-src-note">{s.data_type || '—'} · {s.pulled_by === 'charlie_oscar' ? 'we pull' : 'client provides'}</p>

                <p className="cp-section-label">The file</p>
                {s.file_url ? (
                    <div className="cp-detail-block">
                        <div>{s.source_origin === 'warehouse' ? 'warehouse · ' : 'upload · '}{s.file_name || 'file'}
                            {s.rows ? ` · ${Number(s.rows).toLocaleString()} rows` : ''}</div>
                        <div className="cp-src-note">collected {fmtDate(s.collected_date)}{s.collected_by ? ` by ${s.collected_by}` : ''}{s.owner ? ` · owner ${s.owner}` : ' · no owner'}</div>
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => downloadFile(token, projectId, s.source_id, 'original', s.file_name)}>Download original</button>
                            {s.processed_file_url && <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => downloadFile(token, projectId, s.source_id, 'processed', s.processed_file_name)}>Download processed</button>}
                        </div>
                        {(fileVersions?.versions || fileVersions || []).length > 1 && (
                            <div className="cp-src-note" style={{ marginTop: 6 }}>
                                {(fileVersions?.versions || fileVersions).length} file versions on record
                            </div>
                        )}
                    </div>
                ) : <div className="cp-detail-block cp-src-note">No file yet.</div>}

                <p className="cp-section-label">Requirements</p>
                <div className="cp-detail-block">
                    {s.requirements?.ask ? <div style={{ marginBottom: 6 }}>{s.requirements.ask}</div> : null}
                    {(s.requirements?.required_fields || []).map((f) => <span key={f} className="cp-req-field">{f}</span>)}
                </div>

                <p className="cp-section-label">Processing rules</p>
                {!ruleId ? (
                    <div className="cp-detail-block cp-src-note">No rule set assigned yet — upload a file and "Draft with Bravo" on the Processing stage.</div>
                ) : rulesLoading ? (
                    <div className="cp-detail-block cp-src-note"><span className="cp-spin" /> Loading rules…</div>
                ) : (
                    <div className="cp-detail-block">
                        <div>{ruleId} · v{envelope?.version} · {envelope?.status}
                            {envelope?.confirmed_by ? ` by ${envelope.confirmed_by}` : ''}</div>
                        {editText === null ? (
                            <>
                                <pre className="cp-rules-view">{JSON.stringify(recipe, null, 2)}</pre>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={startEdit}>Edit rules</button>
                                    {envelope?.status === 'draft' && (
                                        <button className="cp-btn cp-btn--sm cp-btn--primary" disabled={confirming} onClick={confirmLatest}>
                                            {confirming ? 'Confirming…' : `Confirm v${envelope.version}`}
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <textarea className="cp-rules-edit" value={editText} rows={18}
                                    onChange={(e) => setEditText(e.target.value)} spellCheck={false} />
                                <div className="cp-src-note" style={{ margin: '6px 0' }}>
                                    Saving creates the next draft version — nothing changes until someone confirms it, recorded against their name.
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button className="cp-btn cp-btn--sm cp-btn--primary" disabled={saving} onClick={saveEdit}>{saving ? 'Saving…' : 'Save as new draft'}</button>
                                    <button className="cp-btn cp-btn--sm cp-btn--ghost" onClick={() => setEditText(null)}>Cancel</button>
                                </div>
                            </>
                        )}
                        {msg && <div className="cp-src-note" style={{ marginTop: 8 }}>{msg}</div>}
                        {savedDraft && (
                            <button className="cp-btn cp-btn--sm cp-btn--primary" style={{ marginTop: 8 }} disabled={confirming} onClick={confirmLatest}>
                                {confirming ? 'Confirming…' : `Confirm v${savedDraft.version} now`}
                            </button>
                        )}
                    </div>
                )}

                <p className="cp-section-label">Status & blocks</p>
                <div className="cp-detail-block">
                    {s.processed && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                            <span>Processed ✓</span>
                            <button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={toggling}
                                onClick={async () => { try { await toggleField({ projectId, sourceId: s.source_id, field: 'processed' }).unwrap(); } catch (e) { setMsg(`⚠ ${errMsg(e)}`); } }}>
                                Un-mark processed
                            </button>
                        </div>
                    )}
                    {s.processing_issue_status === 'open' ? (
                        <div>
                            <div style={{ color: 'var(--co-neg)', marginBottom: 6 }}>Blocked: {s.processing_issue || 'no reason recorded'}</div>
                            <button className="cp-btn cp-btn--sm cp-btn--primary" disabled={issueBusy}
                                onClick={async () => { try { await updateIssue({ projectId, sourceId: s.source_id, status: 'resolved' }).unwrap(); } catch (e) { setMsg(`⚠ ${errMsg(e)}`); } }}>
                                {issueBusy ? 'Unblocking…' : 'Mark resolved / unblock'}
                            </button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input value={blockText} onChange={(e) => setBlockText(e.target.value)}
                                placeholder="Reason (e.g. wrong spend currency — awaiting client)" style={{ flex: 1 }} />
                            <button className="cp-btn cp-btn--sm cp-btn--ghost" disabled={issueBusy || !blockText.trim()}
                                onClick={async () => { try { await updateIssue({ projectId, sourceId: s.source_id, issue_text: blockText.trim(), status: 'open' }).unwrap(); setBlockText(''); } catch (e) { setMsg(`⚠ ${errMsg(e)}`); } }}>
                                Mark blocked
                            </button>
                        </div>
                    )}
                    {(s.quality_flags || []).map((q, i) => <div key={i} className="cp-src-note" style={{ marginTop: 6 }}>⚑ {typeof q === 'string' ? q : q.message || JSON.stringify(q)}</div>)}
                </div>

                <button className="cp-btn cp-btn--ghost" style={{ marginTop: 16 }} onClick={onClose}>Close</button>
            </aside>
        </>
    );
}
